// react
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import StackedBarChartRoundedIcon from '@mui/icons-material/StackedBarChartRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// feature-components
import EnergyAnalyzerFilterInput from '@/features/energyxplorer/components/analysis/EnergyAnalyzerFilterInput';
import HourAnalysisChart from '@/features/energyxplorer/components/analysis/HourAnalysisChart';

// feature-hooks
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';
import useUrlAnalysisScope from '@/features/energyxplorer/hooks/useUrlAnalysisScope';
import useUrlFilters from '@/features/energyxplorer/hooks/useUrlFilters';
import useUrlSeu from '@/features/energyxplorer/hooks/useUrlSeu';
import useUrlViewMode from '@/features/energyxplorer/hooks/useUrlViewMode';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import {
  useGetHourAnalysisDisaggregationQuery,
  useGetHourAnalysisQuery,
  useGetSeuHourAnalysisQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';

import { DateTime } from 'luxon';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import EnergyTreeNodeTraversal from '@/features/energyxplorer/components/nodes/EnergyTreeNodeTraversal';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';

// relative
import EnergyTreeNodeAnalysisToolbar from './EnergyTreeNodeAnalysisToolbar';

function shouldSkipQuery({ filters, selectedEnergyGroup, energyTreeNode }) {
  if (!energyTreeNode) return true;

  const hasActiveDaysFilter =
    filters &&
    Object.prototype.hasOwnProperty.call(filters, 'activeDaysFilter');

  const isPortfolio = selectedEnergyGroup?.type?.includes('Portfolio') === true;

  const hasOpeningHours = !!energyTreeNode?.options?.openingHours;

  // We SKIP when portfolio nodes lack opening hours AND no activeDaysFilter
  return isPortfolio && !hasOpeningHours && hasActiveDaysFilter;
}

const EnergyTreeNodeHourAnalysisPage = () => {
  const dispatch = useDispatch();
  const { accountId, energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );
  const { nodeId, energyTreeNode, setNodeId } = useEnergyTreeNode({
    extraSkip: !selectedEnergyGroup, // don’t fetch if group not loaded
  });
  const { timeRange } = useUrlDateRange();
  const { filters: hourAnalysisFilters } = useUrlFilters({}); // default filters here if you have them
  const { unit } = useEnergyGroupUnit();
  // view mode in URL
  const [viewMode, setViewMode] = useUrlViewMode({
    allowed: ['column', 'box', 'stacked_column'],
    fallback: 'stacked_column',
  });

  // analysis mode in URL
  const [analysisScope] = useUrlAnalysisScope({
    allowed: ['node', 'seu'],
    fallback: 'node',
  });

  const selectedRange = useMemo(
    () => [
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ],
    [timeRange],
  );

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([, nodes]) =>
        nodes.some((node) => node?.dataStreams?.length > 0),
      )
      .map(([type]) => type)
      .filter(
        (type) =>
          type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
      );
  }, [nodeGroupsByType]);

  const [seuToAnalyse] = useUrlSeu({
    key: 'type',
    allowed: validSeuOptions,
    fallback: () => validSeuOptions[0] ?? null,
  });

  const shouldSkipDataFetch = shouldSkipQuery({
    filters: hourAnalysisFilters,
    selectedEnergyGroup,
    energyTreeNode,
  });

  const {
    data: hourAnalysisDisaggregationData,
    isLoading: isHourAnalysisDisaggregationLoading,
    isFetching: isHourAnalysisDisaggregationFetching,
    isUninitialized: isHourAnalysisDisaggregationUninitialized,
    error: hourAnalysisDisaggregationError,
  } = useGetHourAnalysisDisaggregationQuery(
    {
      accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      nodeId: nodeId,
      start: selectedRange[0].toMillis(),
      end: selectedRange[1].toMillis(),
      ...hourAnalysisFilters,
    },
    {
      skip:
        !nodeId ||
        analysisScope !== 'node' ||
        viewMode !== 'stacked_column' ||
        shouldSkipDataFetch,
    },
  );

  const {
    data: hourAnalysisData,
    isLoading: isHourAnalysisLoading,
    isFetching: isHourAnalysisFetching,
    isUninitialized: isHourAnalysisUninitialized,
    error: hourAnalysisError,
  } = useGetHourAnalysisQuery(
    {
      accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      nodeId: nodeId,
      start: selectedRange[0].toMillis(),
      end: selectedRange[1].toMillis(),
      ...hourAnalysisFilters,
    },
    {
      skip:
        !nodeId ||
        analysisScope !== 'node' ||
        viewMode === 'stacked_column' ||
        shouldSkipDataFetch,
    },
  );

  const {
    data: seuHourAnalysisData,
    isLoading: isSeuHourAnalysisLoading,
    isFetching: isSeuHourAnalysisFetching,
    isUninitialized: isSeuHourAnalysisUninitialized,
    error: seuHourAnalysisError,
  } = useGetSeuHourAnalysisQuery(
    {
      accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      seu: seuToAnalyse,
      start: selectedRange[0].toMillis(),
      end: selectedRange[1].toMillis(),
      ...hourAnalysisFilters,
    },
    {
      skip: !seuToAnalyse || analysisScope !== 'seu' || shouldSkipDataFetch,
    },
  );

  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const [data, setData] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  useEffect(() => {
    if (!selectedEnergyGroup) return;
    const energyTreeNodes = selectedEnergyGroup?.energyTreeNodes ?? [];

    if (energyTreeNodes.length === 0) {
      if (nodeId !== null) {
        setNodeId(null);
      }
      return;
    }

    const nodeStillExists = energyTreeNodes.some(
      (n) => n.energyTreeNodeId === nodeId,
    );

    if (!nodeStillExists) {
      setNodeId(energyTreeNodes[0].energyTreeNodeId);
    }
  }, [selectedEnergyGroup, energyTreeNode, nodeId, setNodeId]);

  useEffect(() => {
    if (viewMode === 'stacked_column' && analysisScope === 'seu') {
      setViewMode('column');
    }
  }, [analysisScope, viewMode, setViewMode]);

  useEffect(() => {
    const updateDataAsync = async (nextData) => {
      setData(nextData ?? null);
    };

    if (shouldSkipDataFetch) {
      updateDataAsync(null);
      return;
    }

    // Helper: clear data on error (snackbar handled by apiErrorMiddleware)
    const handleError = () => {
      updateDataAsync(null);
    };

    if (analysisScope === 'seu') {
      if (seuHourAnalysisError) {
        handleError(seuHourAnalysisError);
        return;
      }
      updateDataAsync(seuHourAnalysisData);
      return;
    }

    if (analysisScope === 'node') {
      if (!energyTreeNode) {
        updateDataAsync(null);
        return;
      }

      if (viewMode === 'stacked_column') {
        if (hourAnalysisDisaggregationError) {
          handleError(hourAnalysisDisaggregationError);
          return;
        }
        updateDataAsync(hourAnalysisDisaggregationData);
        return;
      }

      // All other node view modes
      if (hourAnalysisError) {
        handleError(hourAnalysisError);
        return;
      }
      updateDataAsync(hourAnalysisData);
      return;
    }

    // Fallback: unknown scope -> clear data
    updateDataAsync(null);
  }, [
    analysisScope,
    dispatch,
    energyTreeNode,
    hourAnalysisData,
    hourAnalysisDisaggregationData,
    hourAnalysisDisaggregationError,
    hourAnalysisError,
    seuHourAnalysisData,
    seuHourAnalysisError,
    seuToAnalyse,
    shouldSkipDataFetch,
    t,
    viewMode,
  ]);

  const noQueryIsInitialized =
    (analysisScope === 'node' &&
      ((viewMode === 'stacked_column' &&
        isHourAnalysisDisaggregationUninitialized) ||
        (viewMode !== 'stacked_column' && isHourAnalysisUninitialized))) ||
    (analysisScope === 'seu' && isSeuHourAnalysisUninitialized);

  const showLoading =
    isHourAnalysisDisaggregationFetching ||
    isHourAnalysisDisaggregationLoading ||
    isHourAnalysisFetching ||
    isHourAnalysisLoading ||
    isSeuHourAnalysisLoading ||
    isSeuHourAnalysisFetching ||
    noQueryIsInitialized;

  const readyToRender =
    energyTreeNode && selectedEnergyGroup && hourAnalysisFilters;

  return (
    <PageContainer sx={{ pt: 0 }}>
      {readyToRender && (
        <Grid container spacing={2}>
          <Grid
            sx={{ transition: 'flex-basis 0.3s ease' }}
            size={{
              md: 12,
              lg: filtersOpen ? 9 : 12,
            }}
          >
            <Card variant="outlined">
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    {analysisScope === 'node'
                      ? `${energyTreeNode?.name} (Node)`
                      : `${seuToAnalyse} (SEU)`}
                    <InfoTooltipIcon
                      title={t('hour_analysis.hour_analysis_description')}
                    />
                  </Box>
                }
                subheader={
                  selectedRange[0] && selectedRange[1]
                    ? `${t('hour_analysis.in_period')} ${formatDate(
                        selectedRange[0],
                      )} - ${formatDate(selectedRange[1])}`
                    : t('hour_analysis.select_period')
                }
                action={
                  <EnergyTreeNodeAnalysisToolbar
                    filtersOpen={filtersOpen}
                    onToggleFilters={() => setFiltersOpen(!filtersOpen)}
                    showFilterToggle
                    filterToggleTooltip={{
                      open: t('hour_analysis.tooltips.hide'),
                      closed: t('hour_analysis.tooltips.show'),
                    }}
                    actions={
                      <ToggleButtonGroup
                        className="non-printable"
                        value={viewMode}
                        exclusive
                        onChange={(event, newViewMode) => {
                          if (newViewMode !== null) {
                            setViewMode(newViewMode);
                          }
                        }}
                        size="small"
                      >
                        {analysisScope === 'node' && (
                          <ToggleButton value="stacked_column">
                            <StackedBarChartRoundedIcon
                              fontSize="small"
                              sx={{ mr: 1 }}
                            />
                            {t(
                              'hour_analysis.tooltips.show_as_stacked_columns',
                            )}
                          </ToggleButton>
                        )}
                        <ToggleButton value="column">
                          <BarChartRoundedIcon
                            fontSize="small"
                            sx={{ mr: 1 }}
                          />
                          {t('hour_analysis.tooltips.show_as_columns')}
                        </ToggleButton>
                        <ToggleButton value="box">
                          <TuneRoundedIcon
                            sx={{ transform: 'rotate(90deg)', mr: 1 }}
                            fontSize="small"
                          />
                          {t('hour_analysis.tooltips.show_as_boxplot')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    }
                  />
                }
              />
              <CardContent>
                <HourAnalysisChart
                  data={data}
                  viewMode={viewMode}
                  isLoading={showLoading}
                  unit={unit}
                  querySkipped={shouldSkipDataFetch}
                />
              </CardContent>
            </Card>
          </Grid>

          {filtersOpen && (
            <Grid
              className="non-printable"
              size={{
                md: 12,
                lg: 3,
              }}
            >
              <Grid size="grow">
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1">
                        {t('hour_analysis.filtering')}
                      </Typography>
                      <Tooltip
                        title={t('hour_analysis.tooltips.use_filtering')}
                        placement="right"
                      >
                        <InfoRoundedIcon
                          fontSize="small"
                          sx={{
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'lightgray'
                                : 'gray',
                            ml: 1,
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Suspense
                      fallback={
                        <div>
                          {t('analysis.loading', { ns: 'energyxplorer' })}
                        </div>
                      }
                    >
                      <EnergyAnalyzerFilterInput
                        selectedEnergyGroup={selectedEnergyGroup}
                        scope="hour"
                      />
                    </Suspense>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size="grow">
                <EnergyTreeNodeTraversal showModeToggle={true} />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default EnergyTreeNodeHourAnalysisPage;
